import React, { useState } from "react";
import Layout from "../components/Layout";

function GettingStarted() {
  const [completedSteps, setCompletedSteps] = useState(new Set());

  const toggleStep = (stepId) => {
    const newCompleted = new Set(completedSteps);
    if (newCompleted.has(stepId)) {
      newCompleted.delete(stepId);
    } else {
      newCompleted.add(stepId);
    }
    setCompletedSteps(newCompleted);
  };

  const gettingStartedSteps = [
    {
      id: "first-login",
      title: "Your First Login",
      description: "Finish the setup of your account and Log in",
      estimatedTime: "3 minutes",
      icon: "bi-door-open",
      color: "success",
      details: [
        "Follow the link given in the email from webVenly (TRN or PRD)",
        "Enter a new password and then confirm password",
        "Enter email and password",
        "Accept the Acknowledgment",
      ],
      videoUrl: "https://www.youtube.com/embed/pp0kUQNJjFs",
      videoTitle: "How to Access Your WebVenly Environment",
      videoYouTubeLink: "https://youtu.be/pp0kUQNJjFs",
    },
    {
      id: "dashboard",
      title: "Explore Your Environment",
      description: "Get familiar with the main interface",
      estimatedTime: "13 minutes",
      icon: "bi-speedometer2",
      color: "info",
      details: [
        "Review the main navigation menu on the left",
        "Check out the dashboard widgets showing your data",
        "Click through each section to see what's available",
      ],
      videoUrl: "https://www.youtube.com/embed/RsXRlhj1XfI?si=d0R_LguThblNSlom",
      videoTitle: "Exploring Webvenly for Job Coach",
      videoYouTubeLink: "https://youtu.be/RsXRlhj1XfI",
    },
    {
      id: "plan",
      title: "Creating a Plan",
      description: "Create a CRP-20, CRP Service Plan.",
      estimatedTime: "4 minutes",
      icon: "bi-phone",
      color: "warning",
      details: [
        "Access via Job Processing, Forms and select CRP-20",
        "Creating a CRP-20",
      ],
      videoUrl: "https://www.youtube.com/embed/ABs6W5Cc5rU?si=WftaJjHCCKKDEPmw",
      videoTitle: "Create CPR-20, Plan",
      videoYouTubeLink: "https://youtu.be/ABs6W5Cc5rU",
    },
    {
      id: "service",
      title: "Creating a Service Record",
      description: "Create a hourly service record.",
      estimatedTime: "4 minutes",
      icon: "bi-phone",
      color: "primary",
      details: [
        "Log in with your credentials",
        "Nagivate to the Job Log Page",
        "Understanding billable and non-billable",
        "Create your first hourly service (job) log",
      ],
      videoUrl: "https://www.youtube.com/embed/ogIb05JM63U?si=-YpVbpMS78aFDpA6",
      videoTitle: "Creating a houly job (service) log",
      videoYouTubeLink: "https://youtu.be/ogIb05JM63U",
    },
    {
      id: "mileage",
      title: "Create a Mileage Record",
      description: "Build and use mileage routes",
      estimatedTime: "9 minutes",
      icon: "bi-journal-plus",
      color: "danger",
      details: [
        "Create a location",
        "Build, save, and retreive a mileage route",
        "Discuss billable and non-billable",
        "Save and review your entry",
      ],
      videoUrl: "https://www.youtube.com/embed/ZgUYuKS9NJg?si=kgfN58dJN1SjbaOB",
      videoTitle: "Creating a mileage (route) log",
      videoYouTubeLink: "https://youtu.be/ZgUYuKS9NJg",
    },
    {
      id: "report",
      title: "Create a Monthly Report",
      description: "CRP-22, CRP Projress Report",
      estimatedTime: "8 minutes",
      icon: "bi-journal-plus",
      color: "secondary",
      details: [
        "Select a consumer from your list",
        "Choose the appropriate authorization",
        "Update all the fields",
        "Practice using the signature capture",
        "Save and review your entry",
      ],
      videoUrl: "https://www.youtube.com/embed/Wr87eDm1yr0?si=8mUqHfXJPzJzHY8a",
      videoTitle: "Exploring Webvenly for Job Coach",
      videoYouTubeLink: "https://youtu.be/Wr87eDm1yr0",
    },
  ];

  const quickLinks = [
    {
      title: "Training Environment",
      url: "https://trn.webvenly.com",
      description: "Practice safely without affecting real data",
      icon: "bi-mortarboard",
      color: "primary",
    },
    {
      title: "Production Environment",
      url: "https://prd.webvenly.com",
      description: "Live system for official work",
      icon: "bi-building",
      color: "success",
    },
    {
      title: "Schedule a Demo",
      url: "/demo",
      description: "Get personalized help from our team",
      icon: "bi-calendar-check",
      color: "info",
    },
    {
      title: "Contact Support",
      url: "/contact",
      description: "Questions? We're here to help",
      icon: "bi-headset",
      color: "warning",
    },
  ];

  const faqs = [
    {
      question: "What's the difference between TRN and PRD?",
      answer:
        "TRN (Training) is for practice and learning. PRD (Production) is for real work. Always start with TRN to get comfortable before using PRD.",
    },
    {
      question: "Can I use WebVenly on my personal phone?",
      answer:
        "Yes! WebVenly works on any device with a web browser. No app download required - just bookmark the website.",
    },
    {
      question: "What if I forget my password?",
      answer:
        "On the login screen there is a reset password link.  This will allow you to reset your password.",
    },
    {
      question: "How do I know if my log entries are saved?",
      answer:
        "Successfully saved entries will appear in your List forms. If you're unsure, check with your supervisor.",
    },
  ];

  // Inline styles object
  const styles = {
    stepNumber: {
      width: "40px",
      height: "40px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontWeight: "bold",
      fontSize: "0.9rem",
    },
    videoPlaceholder: {
      minHeight: "120px",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
    },
    stepCard: {
      position: "relative",
    },
    bgGradientPrimary: {
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    },
    bgGradientLight: {
      background: "linear-gradient(135deg, #f1f2f6 0%, #dfe6e9 100%)",
    },
  };

  return (
    <Layout>
      <div className="min-vh-100 d-flex flex-column">
        {/* Navbar */}
        {/* <nav className="navbar navbar-expand-lg navbar-dark bg-dark sticky-top">
          <div className="container">
            <a className="navbar-brand fw-bold" href="/">
              <i className="bi bi-layers me-2"></i>
              WebVenly
            </a>
            <div className="d-flex gap-2">
              <a href="/videos" className="btn btn-outline-light btn-sm">
                <i className="bi bi-arrow-left me-2"></i>
                Training Videos
              </a>
              <a href="/" className="btn btn-outline-light btn-sm">
                Home
              </a>
            </div>
          </div>
        </nav> */}

        {/* Hero Section */}
        <section className="text-white py-5" style={styles.bgGradientPrimary}>
          <div className="container">
            <div className="row align-items-center">
              <div className="col-lg-8 mx-auto text-center">
                <h1 className="display-4 fw-bold mb-4">
                  Getting Started with WebVenly
                </h1>
                <p className="lead mb-4">
                  Your step-by-step guide to mastering CRP management in under
                  40 minutes
                </p>
                <div className="d-flex justify-content-center gap-3 flex-wrap">
                  <div className="badge bg-primary bg-opacity-75 px-3 py-2">
                    <i className="bi bi-clock me-2"></i>
                    40 Minutes Total
                  </div>
                  <div className="badge bg-success bg-opacity-75 px-3 py-2">
                    <i className="bi bi-check-circle me-2"></i>6 Simple Steps
                  </div>
                  <div className="badge bg-warning bg-opacity-75 px-3 py-2">
                    <i className="bi bi-phone me-2"></i>
                    Works on Any Device
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-5">
          <div className="container">
            <div className="row">
              {/* Steps Column */}
              <div className="col-lg-8">
                <div className="steps-container">
                  {gettingStartedSteps.map((step, index) => (
                    <div key={step.id} className="mb-4" style={styles.stepCard}>
                      <div
                        className={`card border-${step.color} ${
                          completedSteps.has(step.id)
                            ? "bg-success bg-opacity-10"
                            : ""
                        }`}
                      >
                        <div className="card-header d-flex align-items-center justify-content-between">
                          <div className="d-flex align-items-center">
                            <div
                              className={`bg-${step.color} text-white rounded-circle me-3`}
                              style={styles.stepNumber}
                            >
                              {completedSteps.has(step.id) ? (
                                <i className="bi bi-check-lg"></i>
                              ) : (
                                <span>{index + 1}</span>
                              )}
                            </div>
                            <div>
                              <h5 className="mb-0">{step.title}</h5>
                              <small className="text-muted">
                                <i className="bi bi-clock me-1"></i>
                                {step.estimatedTime}
                              </small>
                            </div>
                          </div>
                        </div>

                        <div className="card-body">
                          <p className="card-text mb-3">{step.description}</p>

                          {/* Video Content */}
                          {step.videoUrl ? (
                            <div className="video-container mb-3">
                              <div className="ratio ratio-16x9">
                                <iframe
                                  src={step.videoUrl}
                                  title={step.videoTitle}
                                  allowFullScreen
                                  className="rounded-3"
                                ></iframe>
                              </div>
                              <div className="mt-2 d-flex justify-content-between align-items-center">
                                <small className="text-muted">
                                  <i className="bi bi-play-circle me-1"></i>
                                  {step.videoTitle}
                                </small>

                                <a
                                  href={step.videoYouTubeLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn btn-sm btn-outline-success"
                                >
                                  <i className="bi bi-box-arrow-up-right me-1"></i>
                                  Watch on YouTube
                                </a>
                              </div>
                            </div>
                          ) : (
                            step.videoPlaceholder && (
                              <div
                                className="bg-light rounded-3 p-4 mb-3 text-center border border-2 border-dashed"
                                style={styles.videoPlaceholder}
                              >
                                <i
                                  className={`bi bi-camera-video text-${step.color} fs-1 mb-2`}
                                ></i>
                                <p className="mb-2 fw-semibold">
                                  Training Video Coming Soon
                                </p>
                                <small className="text-muted">
                                  Step-by-step video guide for "{step.title}"
                                </small>
                              </div>
                            )
                          )}

                          {/* Step Details */}
                          <div className="step-details">
                            <h6 className="fw-semibold mb-2">
                              What you'll do:
                            </h6>
                            <ul className="list-unstyled">
                              {step.details.map((detail, detailIndex) => (
                                <li
                                  key={detailIndex}
                                  className="d-flex align-items-start mb-2"
                                >
                                  <i
                                    className={`bi bi-arrow-right text-${step.color} me-2 mt-1`}
                                  ></i>
                                  <span className="small">{detail}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Sidebar */}
              <div className="col-lg-4">
                {/* Quick Links */}
                <div className="card mb-4">
                  <div className="card-header">
                    <h6 className="mb-0">
                      <i className="bi bi-link-45deg me-2"></i>
                      Quick Links
                    </h6>
                  </div>
                  <div className="card-body p-4">
                    {quickLinks.map((link, index) => (
                      <a
                        key={index}
                        href={link.url}
                        className="list-group-item list-group-item-action border-0"
                        target={
                          link.url.startsWith("http") ? "_blank" : "_self"
                        }
                        rel={
                          link.url.startsWith("http")
                            ? "noopener noreferrer"
                            : ""
                        }
                      >
                        <div className="d-flex align-items-start">
                          <i
                            className={`${link.icon} text-${link.color} fs-5 me-3 mt-1`}
                          ></i>
                          <div>
                            <div className="fw-semibold">{link.title}</div>
                            <small className="text-muted">
                              {link.description}
                            </small>
                          </div>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>

                {/* FAQ */}
                <div className="card">
                  <div className="card-header">
                    <h6 className="mb-0">
                      <i className="bi bi-question-circle me-2"></i>
                      Common Questions
                    </h6>
                  </div>
                  <div className="card-body">
                    <div
                      className="accordion accordion-flush"
                      id="faqAccordion"
                    >
                      {faqs.map((faq, index) => (
                        <div key={index} className="accordion-item border-0">
                          <h2 className="accordion-header">
                            <button
                              className="accordion-button collapsed p-0 pb-2 border-0 bg-transparent shadow-none"
                              type="button"
                              data-bs-toggle="collapse"
                              data-bs-target={`#faq${index}`}
                            >
                              <small className="fw-semibold">
                                {faq.question}
                              </small>
                            </button>
                          </h2>
                          <div
                            id={`faq${index}`}
                            className="accordion-collapse collapse"
                            data-bs-parent="#faqAccordion"
                          >
                            <div className="accordion-body p-0 pt-2 pb-3">
                              <small className="text-muted">{faq.answer}</small>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Next Steps Section */}
        <section className="py-5" style={styles.bgGradientLight}>
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-8 text-center">
                <h2 className="mb-4">Ready for Advanced Training?</h2>
                <p className="lead mb-4">
                  Once you've completed the basics, explore role-specific
                  training content
                </p>
                <div className="row g-3">
                  <div className="col-md-6">
                    <a
                      href="/adminfeatures"
                      className="btn btn-outline-warning w-100"
                    >
                      <i className="bi bi-gear me-2"></i>
                      Admin Training
                    </a>
                  </div>
                  <div className="col-md-6">
                    <a href="/videos" className="btn btn-outline-info w-100">
                      <i className="bi bi-graph-up me-2"></i>
                      Training Videos
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-dark text-white py-4 mt-auto">
          <div className="container text-center">
            <p className="mb-1">
              &copy; {new Date().getFullYear()} WebVenly. All rights reserved.
            </p>
            <p className="mb-0 small text-light">
              Powered by <strong>Wyatt Multi Solutions</strong>
            </p>
          </div>
        </footer>
      </div>
    </Layout>
  );
}

export default GettingStarted;
